// src/mocks/demoStore.ts
// One in-memory store for the whole demo (events, products, assets, links), seeded from simbDemoData.json.
// The asset pages use it directly; the other pages reach it through the demo API (demoApi.ts).
// Persisted to localStorage so the demo survives refreshes and works across tabs.
import { demoData, type DemoEvent, type DemoProduct } from "./demoData";
import type { Asset, AssetShare, SupportTicket, UploadAttemptLog } from "../modules/assetManagement/types";

export interface StoreEvent extends DemoEvent {
  imageUrl?: string;
  videoUrl?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StoreProduct extends DemoProduct {
  imageUrl?: string;
  videoUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MockState {
  events: StoreEvent[];
  products: StoreProduct[];
  assets: Asset[];
  uploadLog: UploadAttemptLog[];
  shares: AssetShare[];
  tickets: SupportTicket[];
}

const STORAGE_KEY = "simb_demo_store_v3";
/** Key the Product create/edit page (ProductFormPage) reads and writes */
const PRODUCT_FORM_KEY = "mockProducts";

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

const seed = (): MockState => ({
  events: clone(demoData.events).map((e) => ({ ...e, isActive: true })),
  products: clone(demoData.products),
  assets: clone(demoData.assets),
  uploadLog: clone(demoData.uploadLog),
  shares: [],
  tickets: [],
});

const load = (): MockState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as MockState;
  } catch {
    /* storage unavailable: fall back to seed */
  }
  return seed();
};

let state: MockState = load();
const listeners = new Set<() => void>();

// ---- sync with ProductFormPage (it keeps products in localStorage("mockProducts")) ----

let lastPushed = "";
/** Only demo mode keeps ProductFormPage's localStorage in step with this store */
let productFormSync = false;

const productFormShape = (s: MockState) => ({
  products: s.products.map((p) => ({
    id: p.id,
    name: p.title,
    title: p.title,
    description: p.description,
    launchDate: p.launchDate,
    endDate: p.endDate ?? "",
    notes: p.notes,
    status: p.status,
    image: p.imageUrl ?? "",
    tags: p.tags,
    events: s.events
      .filter((e) => e.isActive !== false && e.productIds.includes(p.id))
      .map((e) => ({ id: e.id, name: e.title })),
    assets: s.assets.filter((a) => a.productIds.includes(p.id)).map((a) => ({ id: a.id, name: a.filename })),
  })),
  availableEvents: s.events.filter((e) => e.isActive !== false).map((e) => ({ id: e.id, name: e.title })),
  availableAssets: s.assets.map((a) => ({ id: a.id, name: a.filename })),
});

const pushProductForm = () => {
  if (!productFormSync) return;
  try {
    lastPushed = JSON.stringify(productFormShape(state));
    localStorage.setItem(PRODUCT_FORM_KEY, lastPushed);
  } catch {
    /* ignore */
  }
};

/** Apply products created or edited on ProductFormPage since our last write */
const pullProductForm = () => {
  if (!productFormSync) return;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PRODUCT_FORM_KEY);
  } catch {
    return;
  }
  if (!raw || raw === lastPushed) return;
  let form: ReturnType<typeof productFormShape>;
  try {
    form = JSON.parse(raw);
  } catch {
    return;
  }
  const now = new Date().toISOString();
  let next = { ...state, products: [...state.products], events: [...state.events], assets: [...state.assets] };
  (form.products || []).forEach((fp: any) => {
    const id = String(fp.id);
    const fields = {
      title: fp.name || fp.title || "Untitled product",
      description: fp.description ?? "",
      notes: fp.notes ?? "",
      launchDate: fp.launchDate ?? "",
      endDate: fp.endDate || null,
      status: fp.status === "Archived" ? ("Archived" as const) : ("Active" as const),
      tags: Array.isArray(fp.tags) ? fp.tags : [],
      imageUrl: fp.image || "",
    };
    const i = next.products.findIndex((p) => p.id === id);
    if (i >= 0) next.products[i] = { ...next.products[i], ...fields, updatedAt: now };
    else next.products.unshift({ id, ...fields, createdAt: now, updatedAt: now });

    const eventIds = new Set((fp.events || []).map((e: any) => String(e.id)));
    next.events = next.events.map((e) => {
      const has = e.productIds.includes(id);
      if (eventIds.has(e.id) === has) return e;
      return { ...e, productIds: has ? e.productIds.filter((x) => x !== id) : [...e.productIds, id] };
    });
    const assetIds = new Set((fp.assets || []).map((a: any) => String(a.id)));
    next.assets = next.assets.map((a) => {
      const has = a.productIds.includes(id);
      if (assetIds.has(a.id) === has) return a;
      return { ...a, productIds: has ? a.productIds.filter((x) => x !== id) : [...a.productIds, id] };
    });
  });
  state = next;
  persist(false);
};

// ---- persistence ----------------------------------------------------------------

const persist = (notify = true) => {
  try {
    // blob: previews only live for this page load, so they are not stored
    const toStore: MockState = {
      ...state,
      assets: state.assets.map((a) => (a.previewUrl?.startsWith("blob:") ? { ...a, previewUrl: null } : a)),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch {
    /* ignore quota / private mode */
  }
  pushProductForm();
  if (notify) listeners.forEach((l) => l());
};

export const enableProductFormSync = () => {
  productFormSync = true;
  pushProductForm();
};

export const getState = (): MockState => {
  pullProductForm();
  return state;
};

export const setState = (updater: (s: MockState) => MockState) => {
  pullProductForm();
  state = updater(state);
  persist();
};

export const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetMockState = () => setState(() => seed());
